import { IEngineOutage } from "../../shared/types/operate/IEngineOutage";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GasCommercialOverviewUrl, GasEngineOverviewUrl } from "../../shared/utils/DispathWebUtils"
import { Box, Button, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import Grid from '@mui/material/Unstable_Grid2'; // Grid version 2
import { ArrowDownward, ArrowUpward, Download, Restore } from "@mui/icons-material";
import { Dictionary } from "@reduxjs/toolkit";
import EditIcon from '@mui/icons-material/Edit';
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material";
import { DownloadFileNames, downloadXlsxData } from "../../shared/helpers/downloadXlsxDataHelper";

const headCells: HeadCell[] = [
    {
        id: "remitID",
        label: "Remit ID"
    },

    {
        id: "siteID",
        label: "Site ID"
    },
    {
        id: "engineID",
        label: "Engine ID"
    },
    {
        id: "outageStart",
        label: "Outage Start (Local Time)"
    },
    {
        id: "outageEnd",
        label: "Outage End (Local Time)"
    },
    {
        id: "mwOutage",
        label: "Outage MW"
    },
    {
        id: "comments",
        label: "Reason"
    },
    {
        id: "live",
        label: "Live"
    },
    {
        id: "restriction",
        label: "Outage/Restriction"
    },
    {
        id: "created",
        label: "Created (Local Time)"
    },
    {
        id: "createdBy",
        label: "Created By"
    },
    {
        id: "lastUpdatedBy",
        label: "Last Updated By"
    },
    {
        id: "workOrder",
        label: "EAM Work Order"
    },
    {
        id: "breakdown",
        label: "Breakdown of Failures"
    }
]

const EngineOutageTable: React.FC<IEngineOutageTableProps> = ({ items, siteIdToName, searchBar, maxHeight = 440, restoreOutage, downloadButton = false, fileName, isRestoreOutagePage = false }) => {
    const [orderDescending, setOrderDescending] = useState<boolean>(false);
    const [orderCategory, setOrderCategory] = useState<keyof IEngineOutage>("remitID");
    const [searchContents, setSearchContents] = useState<string>("");
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const navigate = useNavigate();
    const theme = useTheme()

    const formatEmailAddresses = (email: string) => {
        let formattedEmail = email

        let atIndex = formattedEmail.indexOf("@")
        if (atIndex >= 0) {
            formattedEmail = formattedEmail.substring(0, atIndex)
        }

        formattedEmail = formattedEmail.replace(".", " ")

        return formattedEmail
    }

    const sortItems = (inputArray: IEngineOutage[], category: keyof IEngineOutage, descending: boolean) => {
        inputArray.sort((a, b) => {
            const aValue = a[category]
            const bValue = b[category]
            if (aValue === null || aValue === undefined) { return 1 }
            else if (bValue === null || bValue === undefined) { return -1 }
            else if (aValue < bValue) { return -1 }
            return 1
        })
        if (descending) {
            inputArray.reverse()
        }
        return inputArray
    }

    useEffect(() => {
        setInternalItems(sortItems(items, orderCategory, orderDescending))
    }, [items])

    if (items === undefined) {
        items = []
    }

    const [internalItems, setInternalItems] = React.useState<IEngineOutage[]>(sortItems(items, orderCategory, orderDescending))

    const handleSearchContentsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchContents(event.target.value as string)
    }

    const handleSort = (categoryID: keyof IEngineOutage) => {
        let descending = false
        if (orderCategory === categoryID && !orderDescending) {
            descending = true
        }
        setOrderDescending(descending)
        setOrderCategory(categoryID)
        setInternalItems(sortItems(internalItems, categoryID, descending))
    }

    const filterOutage = useCallback((outage: IEngineOutage) => {
        return(
            outage.remitID.toString().startsWith(searchContents) ||
            outage.siteID.toLowerCase().startsWith(searchContents.toLowerCase()) ||
            siteIdToName[outage.siteID]?.toLowerCase().startsWith(searchContents.toLowerCase()) ||
            outage.engineID.toLowerCase().startsWith(searchContents.toLowerCase()) ||
            outage.comments.toLowerCase().startsWith(searchContents.toLowerCase())
        )
    }, [searchContents, siteIdToName])

    const navigateToUpdateOutage = (remitId: string) => {
        navigate(`/operate/availability/update-engine-outage?RemitID=${remitId}&Restore=${isRestoreOutagePage}`);
    }

    const filteredItems = useMemo(() => {
        return internalItems.filter((item) => filterOutage(item))
    }, [filterOutage, internalItems])

    const columnNames = [
        { key: "remitID", displayLabel: "Remit ID", columnWidth: 18, isDecimalFormat: false, isTotal: false, isAverage: false, isSummaryRowRequired: false },
        { key: "siteID", displayLabel: "Site ID", columnWidth: 20, isDecimalFormat: false, isTotal: false, isAverage: false, isSummaryRowRequired: false },
        { key: "engineID", displayLabel: "Engine ID", columnWidth: 20, isDecimalFormat: false, isTotal: false, isAverage: false, isSummaryRowRequired: false },
        { key: "outageStartLocal", displayLabel: "Start (Local Time)", columnWidth: 20, isDecimalFormat: false, isTotal: false, isAverage: false, isSummaryRowRequired: false },
        { key: "outageEndLocal", displayLabel: "End (Local Time)", columnWidth: 20, isDecimalFormat: false, isTotal: false, isAverage: false, isSummaryRowRequired: false },
        { key: "outageStartUTC", displayLabel: "Start (UTC)", columnWidth: 20, isDecimalFormat: false, isTotal: false, isAverage: false, isSummaryRowRequired: false },
        { key: "outageEndUTC", displayLabel: "End (UTC)", columnWidth: 20, isDecimalFormat: false, isTotal: false, isAverage: false, isSummaryRowRequired: false },
        { key: "mwOutage", displayLabel: "Outage MW", columnWidth: 20, isDecimalFormat: true, isTotal: false, isAverage: false, isSummaryRowRequired: false },
        { key: "comments", displayLabel: "Reason", columnWidth: 20, isDecimalFormat: false, isTotal: false, isAverage: false, isSummaryRowRequired: false },
        { key: "live", displayLabel: "Live", columnWidth: 20, isDecimalFormat: false, isTotal: false, isAverage: false, isSummaryRowRequired: false },
        { key: "created", displayLabel: "Last Updated (UTC)", columnWidth: 20, isDecimalFormat: false, isTotal: false, isAverage: false, isSummaryRowRequired: false },
        { key: "modifiedParent", displayLabel: "Modified Parent", columnWidth: 20, isDecimalFormat: false, isTotal: false, isAverage: false, isSummaryRowRequired: false },
        { key: "detail", displayLabel: "Comments", columnWidth: 20, isDecimalFormat: false, isTotal: false, isAverage: false, isSummaryRowRequired: false },
        { key: "outageType", displayLabel: "Reactive/Planned", columnWidth: 20, isDecimalFormat: false, isTotal: false, isAverage: false, isSummaryRowRequired: false },
        { key: "restriction", displayLabel: "Outage/Restriction", columnWidth: 20, isDecimalFormat: false, isTotal: false, isAverage: false, isSummaryRowRequired: false },
        { key: "createdBy", displayLabel: "Created By", columnWidth: 20, isDecimalFormat: false, isTotal: false, isAverage: false, isSummaryRowRequired: false },
        { key: "lastUpdatedBy", displayLabel: "Last Updated By", columnWidth: 20, isDecimalFormat: false, isTotal: false, isAverage: false, isSummaryRowRequired: false },
        { key: "workOrder", displayLabel: "Work Order", columnWidth: 20, isDecimalFormat: false, isTotal: false, isAverage: false, isSummaryRowRequired: false },
        { key: "bucket", displayLabel: "Bucket", columnWidth: 20, isDecimalFormat: false, isTotal: false, isAverage: false, isSummaryRowRequired: false },
        { key: "breakdown", displayLabel: "Breakdown", columnWidth: 20, isDecimalFormat: false, isTotal: false, isAverage: false, isSummaryRowRequired: false },
    ];

    const tableDownloadData = filteredItems?.map((d) => {
        return {
            remitID: d.remitID,
            siteID: d.siteID,
            engineID: d.engineID,
            outageStartLocal: moment(d.outageStart).format("DD-MMM-YY HH:mm:ss"),
            outageEndLocal: moment(d.outageEnd).format("DD-MMM-YY HH:mm:ss"),
            outageStartUTC: moment(d.outageStart).utc().format("DD-MMM-YY HH:mm:ss"),
            outageEndUTC: moment(d.outageEnd).utc().format("DD-MMM-YY HH:mm:ss"),
            mwOutage: d.mwOutage.toFixed(1),
            comments: d.comments,
            live: d.live ? "True" : "False",
            created: moment(d.created).utc().format("DD-MMM-YY HH:mm:ss"),
            modifiedParent: d.modifiedParent,
            detail: d.detail,
            outageType: d.outageType,
            restriction: d.restriction ? "Restriction" : "Outage",
            createdBy: d.createdBy ? formatEmailAddresses(d.createdBy) : null,
            lastUpdatedBy: d.lastUpdatedBy ? formatEmailAddresses(d.lastUpdatedBy) : null,
            workOrder: d.workOrder,
            bucket: d.bucket,
            breakdown: d.breakdown
        };
    }) || [];

    const handleXSLXDownload = async () => {
        setIsDownloading(true);
        const fileNames = fileName ?? {
            titleName: "Outages",
            fileName: "Outages",
            appendCurrentDateTime: true
        }

        let downLoadFileName = fileNames.fileName

        if (fileNames.appendCurrentDateTime) {
            downLoadFileName = `${downLoadFileName}_${moment().toISOString()}`
        }

        try {
            await downloadXlsxData(tableDownloadData, columnNames, fileNames.titleName, "Sheet 1", downLoadFileName);
        } finally {
            setIsDownloading(false);
        }
    }

    return (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            {(searchBar ?
                <Grid container spacing={2} mb={1}>
                    <Grid md={downloadButton ? 10 : 12} xs={12}>
                        <Box>
                            <TextField
                                id="search-label"
                                label="Search"
                                value={searchContents}
                                onChange={handleSearchContentsChange}
                                name="Search"
                                placeholder="Search"
                                fullWidth
                            />
                        </Box>
                    </Grid>
                    {downloadButton && <Grid md={2} xs={12}>
                        <Box sx={{ height: "100%" }}>
                            <Button
                                variant="contained"
                                startIcon={<Download />}
                                onClick={handleXSLXDownload}
                                disabled={isDownloading || filteredItems.length === 0}
                                fullWidth
                                size="large"
                                sx={{ height: "55px" }}
                            >
                                Download XLSX
                            </Button>
                        </Box>
                    </Grid>}
                </Grid>
                : downloadButton ?
                    <Stack direction="row" justifyContent="flex-end" mb={1}>
                        <Box sx={{ height: "100%" }}>
                            <Button
                                variant="contained"
                                startIcon={<Download />}
                                onClick={handleXSLXDownload}
                                disabled={isDownloading || filteredItems.length === 0}
                                fullWidth
                                size="large"
                                sx={{ height: "55px" }}
                            >
                                Download XLSX
                            </Button>
                        </Box>
                </Stack>
                : <></>)}
            <TableContainer sx={{ maxHeight: maxHeight }}>
                <Table stickyHeader size="small" aria-label="sticky table">
                    <TableHead>
                        <TableRow>
                            {headCells.map((headCell) => (
                                <TableCell
                                    align="center"
                                    key={headCell.id}
                                >
                                    <IconButton
                                        size="small"
                                        sx={{ "transition": "all .2s", "color": "inherit", "&:hover": { "backgroundColor": "rgba(160, 160, 160, 0.5)" } }}
                                        onClick={() => handleSort(headCell.id)}
                                    >
                                        <Typography variant="subtitle1" fontSize="small">{headCell.label}</Typography>
                                        {headCell.id === orderCategory ? (orderDescending ?  < ArrowDownward fontSize="small" /> : <ArrowUpward fontSize="small" />) : <></>}
                                    </IconButton>
                                </TableCell>
                            ))}
                            <TableCell align="center"></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            filteredItems
                                .map((item) => {
                                return (
                                    <TableRow key={"outage-table-key-" + item.remitID}>
                                        <TableCell align="center">{item.remitID}</TableCell>
                                        <TableCell align="center"><Button sx={{ width:"100%", color: "white", cursor:"pointer" }} onClick={() => window.open(GasCommercialOverviewUrl(siteIdToName[item.siteID]), '_blank')}>{item.siteID}</Button></TableCell>
                                        <TableCell align="center">{item.engineID.startsWith('G') ? <Button sx={{ width: "100%", color: "white", cursor:"pointer" }} onClick={() => window.open(GasEngineOverviewUrl(siteIdToName[item.siteID], item.engineID), '_blank')}>{item.engineID}</Button> : item.engineID}</TableCell>
                                        <TableCell align="center">{moment(item.outageStart).format("DD-MMM-YY HH:mm:ss")}</TableCell>
                                        <TableCell align="center">{moment(item.outageEnd).format("DD-MMM-YY HH:mm:ss")}</TableCell>
                                        <TableCell align="center">{item.mwOutage.toFixed(1)}</TableCell>
                                        <TableCell align="center">{item.comments}</TableCell>
                                        <TableCell align="center" sx={{ backgroundColor: item.live ? theme.palette.success.dark : theme.palette.error.dark }}>{(item.live ? "True" : "False")}</TableCell>
                                        <TableCell align="center">{(item.restriction ? "Restriction" : "Outage")}</TableCell>
                                        <TableCell align="center">{moment(item.created).format("DD-MMM-YY HH:mm:ss")}</TableCell>
                                        <TableCell align="center">{(item.createdBy ? formatEmailAddresses(item.createdBy) : "-")}</TableCell>
                                        <TableCell align="center">{(item.lastUpdatedBy ? formatEmailAddresses(item.lastUpdatedBy) : "-")}</TableCell>
                                        <TableCell align="center">{(item.workOrder === null ? "-" : item.workOrder.toString())}</TableCell>
                                        <TableCell align="center">{(item.breakdown === "" ? "-" : item.breakdown)}</TableCell>

                                        <TableCell align="center" sx={{ pr: 0 }}>
                                            <Button sx={{ width: "100%" }} onClick={() => navigateToUpdateOutage(item.remitID)}>
                                                <EditIcon />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                )})
                        }
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );

}
interface HeadCell {
    id: keyof IEngineOutage,
    label: string
}
export interface IEngineOutageTableProps {
    items: IEngineOutage[],
    siteIdToName: Dictionary<string>
    searchBar?: boolean,
    maxHeight?: number|string,
    restoreOutage?: (outage: IEngineOutage) => void
    downloadButton?: boolean
    fileName?: DownloadFileNames
    isRestoreOutagePage?: boolean
}

export default EngineOutageTable;